import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  requiresPasswordChange: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  generateOTP: (email: string) => Promise<{ error: any }>;
  signInWithOTP: (email: string, otp: string) => Promise<{ error: any; requiresNewPassword?: boolean }>;
  updatePasswordAfterOTP: (newPassword: string) => Promise<{ error: any }>;
  clearPasswordChangeRequirement: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check if user requires password change after OTP login
        const requiresChange = session?.user?.user_metadata?.requires_password_change === true;
        setRequiresPasswordChange(requiresChange);
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Check if user requires password change after OTP login
      const requiresChange = session?.user?.user_metadata?.requires_password_change === true;
      setRequiresPasswordChange(requiresChange);
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          username: username,
        }
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔐 DEBUG: Starting sign-in process');
    console.log('📧 Email:', email);
    console.log('🔑 Password length:', password?.length || 0);
    console.log('🔑 Password preview:', password ? password.substring(0, 3) + '***' : 'undefined');
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    console.log('📊 Supabase response data:', data);
    console.log('❌ Supabase error:', error);
    
    if (error) {
      console.log('🚨 Sign-in failed with error:', error.message);
    } else {
      console.log('✅ Sign-in successful!');
    }
    
    return { error };
  };

  const signOut = async () => {
    try {
      console.log('🚪 Starting sign out process');
      
      // Sign out from Supabase (clears session and localStorage)
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ Sign out error:', error);
        return { error };
      }
      
      console.log('✅ Sign out successful');
      
      // Clear local state immediately
      setSession(null);
      setUser(null);
      
      return { error: null };
    } catch (error) {
      console.error('💥 Unexpected sign out error:', error);
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const generateOTP = async (email: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('generate-otp', {
        body: { email }
      });

      if (error) {
        console.error('OTP generation error:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('OTP generation failed:', error);
      return { error };
    }
  };

  const signInWithOTP = async (email: string, otp: string) => {
    try {
      // Call edge function for OTP verification and temp password generation
      const { data, error } = await supabase.functions.invoke('signin-with-otp', {
        body: { email, otp }
      });

      if (error) {
        console.error('OTP verification error:', error);
        return { error };
      }

      // Sign in with temporary password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: data.tempPassword,
      });

      if (signInError) {
        return { error: signInError };
      }

      return { error: null, requiresNewPassword: true };
    } catch (error) {
      console.error('OTP sign-in failed:', error);
      return { error };
    }
  };

  const updatePasswordAfterOTP = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        data: {
          requires_password_change: false
        }
      });

      if (!error) {
        setRequiresPasswordChange(false);
      }

      return { error };
    } catch (error) {
      console.error('Password update failed:', error);
      return { error };
    }
  };

  const clearPasswordChangeRequirement = () => {
    setRequiresPasswordChange(false);
  };

  const value = {
    user,
    session,
    loading,
    requiresPasswordChange,
    signUp,
    signIn,
    signOut,
    resetPassword,
    generateOTP,
    signInWithOTP,
    updatePasswordAfterOTP,
    clearPasswordChangeRequirement,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};